import { EnemyId } from "../state/runState";
import {
  applyEnemyScaling,
  EnemyScalingMultipliers,
  getEnemyScalingMultipliers,
} from "../state/enemyScaling";
import { PrimitiveBlueprint } from "../avatars/types";
import { GOBLIN_BLUEPRINT } from "../avatars/blueprints/goblin";
import { BAT_BLUEPRINT } from "../avatars/blueprints/bat";
import { SKELETON_BLUEPRINT } from "../avatars/blueprints/skeleton";
import { SLIME_BLUEPRINT } from "../avatars/blueprints/slime";
import { SPIDER_BLUEPRINT } from "../avatars/blueprints/spider";
import { ENEMIES, EnemyDef, EnemyInstance, createEnemyInstance } from "./enemies";
import { applyPerks } from "./perks";

export type EnemyFactoryOptions = {
  nameSuffix?: string;
  floor?: number;
  scalingMultipliers?: EnemyScalingMultipliers;
};

/**
 * Centralized enemy factory for creation, naming, scaling, and blueprint lookup.
 * Provides a consistent interface for instantiating enemies across the game.
 */
export class EnemyFactory {
  private static blueprintMap: Record<EnemyId, PrimitiveBlueprint> = {
    goblin: GOBLIN_BLUEPRINT,
    bat: BAT_BLUEPRINT,
    skeleton: SKELETON_BLUEPRINT,
    slime: SLIME_BLUEPRINT,
    spider: SPIDER_BLUEPRINT,
  };

  /**
   * Create a single enemy instance from its ID with optional scaling and naming.
   * @param enemyId - The ID of the enemy type to create
   * @param options - Optional configuration for naming and scaling
   * @returns A fully configured EnemyInstance
   */
  static create(enemyId: EnemyId, options: EnemyFactoryOptions = {}): EnemyInstance {
    const def = ENEMIES[enemyId];
    if (!def) {
      throw new Error(`Unknown enemy ID: ${enemyId}`);
    }

    // Create base instance
    const instance = createEnemyInstance(def, options.nameSuffix);

    // Apply scaling if requested
    if (options.floor !== undefined) {
      const multipliers = getEnemyScalingMultipliers(options.floor);
      applyEnemyScaling(instance, multipliers);
    } else if (options.scalingMultipliers) {
      applyEnemyScaling(instance, options.scalingMultipliers);
    }

    // Apply perks after scaling (base → scale → perks sequence)
    if (instance.perks.length > 0) {
      applyPerks(instance, instance.perks);
    }

    return instance;
  }

  /**
   * Create multiple instances of the same enemy type with auto-numbering.
   * @param enemyId - The ID of the enemy type to create
   * @param count - How many instances to create
   * @param options - Optional configuration (floor or scalingMultipliers)
   * @returns Array of numbered enemy instances
   */
  static createGroup(
    enemyId: EnemyId,
    count: number,
    options: Omit<EnemyFactoryOptions, "nameSuffix"> = {}
  ): EnemyInstance[] {
    const instances: EnemyInstance[] = [];
    for (let i = 0; i < count; i++) {
      instances.push(
        EnemyFactory.create(enemyId, {
          ...options,
          nameSuffix: count > 1 ? `#${i + 1}` : undefined,
        })
      );
    }
    return instances;
  }

  /**
   * Get the visual blueprint for rendering an enemy type.
   * @param enemyId - The ID of the enemy type
   * @returns The PrimitiveBlueprint for rendering
   */
  static getBlueprint(enemyId: EnemyId): PrimitiveBlueprint {
    const blueprint = EnemyFactory.blueprintMap[enemyId];
    if (!blueprint) {
      throw new Error(`No blueprint registered for enemy: ${enemyId}`);
    }
    return blueprint;
  }

  /**
   * Get the definition for an enemy type.
   * @param enemyId - The ID of the enemy type
   * @returns The EnemyDef definition
   */
  static getDef(enemyId: EnemyId): EnemyDef {
    const def = ENEMIES[enemyId];
    if (!def) {
      throw new Error(`Unknown enemy ID: ${enemyId}`);
    }
    return def;
  }
}
